import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { format } from 'date-fns'
import { toast } from 'sonner'
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  DollarSign,
  Users,
  FileText,
  ChevronLeft,
  Trophy,
  Info
} from 'lucide-react'
import MilestoneAssignments from './milestone-assignments'
import type { 
  MilestoneWithRelations, 
  MilestoneProgress,
  MilestoneProgressFormData,
  MilestoneStatus
} from '@/types/milestone'
import { usePermissions } from '@/lib/hooks'

interface MilestoneDetailProps {
  milestone: MilestoneWithRelations
  projectId: string
  onBack: () => void
  onEdit: () => void
}

export default function MilestoneDetail({
  milestone,
  projectId,
  onBack,
  onEdit
}: MilestoneDetailProps) {
  const [showProgressDialog, setShowProgressDialog] = useState(false)
  const [showAchieveDialog, setShowAchieveDialog] = useState(false)
  const queryClient = useQueryClient()
  const permissions = usePermissions()

  // Progress form state
  const [progressForm, setProgressForm] = useState<MilestoneProgressFormData>({
    progress_percentage: 0,
    notes: ''
  })

  // Achievement form state
  const [achievementNotes, setAchievementNotes] = useState('')

  // Fetch milestone progress
  const { data: progressHistory } = useQuery({
    queryKey: ['milestone-progress', milestone.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('milestone_progress')
        .select('*')
        .eq('milestone_id', milestone.id)
        .order('progress_date', { ascending: false })

      if (error) throw error
      return (data || []) as MilestoneProgress[]
    }
  })

  // Get latest progress
  const latestProgress = progressHistory?.[0] as MilestoneProgress | undefined
  const currentProgress = latestProgress?.progress_percentage || 0

  // Update progress mutation
  const { mutate: updateProgress, isPending: isUpdatingProgress } = useMutation({
    mutationFn: async (data: MilestoneProgressFormData) => {
      const user = await supabase.auth.getUser()
      
      const { error } = await supabase
        .from('milestone_progress')
        .insert({
          milestone_id: milestone.id,
          progress_date: new Date().toISOString().split('T')[0],
          progress_percentage: data.progress_percentage,
          progress_value: milestone.target_value 
            ? (milestone.target_value * (data.progress_percentage || 0)) / 100 
            : undefined,
          notes: data.notes,
          reported_by: user.data.user?.id
        })

      if (error) throw error

      // Update milestone status if needed
      if (data.progress_percentage && data.progress_percentage > 0 && milestone.status === 'pending') {
        const { error: updateError } = await supabase
          .from('milestones')
          .update({ status: 'in_progress' })
          .eq('id', milestone.id)

        if (updateError) throw updateError
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-progress'] })
      queryClient.invalidateQueries({ queryKey: ['milestones'] })
      setShowProgressDialog(false)
      setProgressForm({ progress_percentage: 0, notes: '' })
      toast.success('Progress updated')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  // Mark as achieved mutation
  const { mutate: markAsAchieved, isPending: isAchieving } = useMutation({
    mutationFn: async (notes: string) => {
      const user = await supabase.auth.getUser()
      
      const { error } = await supabase
        .from('milestones')
        .update({
          status: 'achieved',
          achieved_date: new Date().toISOString().split('T')[0],
          achieved_by: user.data.user?.id,
          verified_by: user.data.user?.id,
          verification_notes: notes
        })
        .eq('id', milestone.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestones'] })
      setShowAchieveDialog(false)
      setAchievementNotes('')
      toast.success('Milestone marked as achieved! Equity grants will be processed.')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const getStatusColor = (status: MilestoneStatus) => {
    switch (status) {
      case 'pending': return 'text-gray-600 bg-gray-100'
      case 'in_progress': return 'text-blue-600 bg-blue-100'
      case 'achieved': return 'text-green-600 bg-green-100'
      case 'expired': return 'text-red-600 bg-red-100'
      case 'cancelled': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const formatValue = (value?: number) => {
    if (!value) return 'N/A'
    
    switch (milestone.milestone_type) {
      case 'revenue':
      case 'fundraising':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value)
      case 'user_growth':
        return value.toLocaleString() + ' users'
      default:
        return value.toLocaleString()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Milestones
          </Button>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{milestone.name}</h2>
            <p className="text-sm text-gray-600">{milestone.description}</p>
          </div>
        </div>
        {permissions.canEditProject && (
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={onEdit}>
              Edit Milestone
            </Button>
            {milestone.status === 'in_progress' && (
              <Button 
                onClick={() => setShowAchieveDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Trophy className="h-4 w-4 mr-2" />
                Mark as Achieved
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <Badge className={getStatusColor(milestone.status)}>
              {milestone.status.replace('_', ' ')}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-gray-500">Target</p>
            <p className="text-lg font-medium">{formatValue(milestone.target_value)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Target Date</p>
            <p className="text-lg font-medium">
              {milestone.target_date 
                ? format(new Date(milestone.target_date), 'MMM d, yyyy')
                : 'Not set'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Equity Reward</p>
            <p className="text-lg font-medium">
              {milestone.equity_amount 
                ? `${milestone.equity_amount.toLocaleString()} shares`
                : milestone.equity_percentage
                ? `${milestone.equity_percentage}%`
                : 'Not set'}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {milestone.status === 'in_progress' && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Progress</span>
              <span className="text-sm text-gray-600">{currentProgress}%</span>
            </div>
            <Progress value={currentProgress} className="h-2" />
            {permissions.canEditProject && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowProgressDialog(true)}
                className="mt-2"
              >
                Update Progress
              </Button>
            )}
          </div>
        )}

        {/* Achievement Info */}
        {milestone.status === 'achieved' && milestone.achieved_date && (
          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-green-900">
                  Achieved on {format(new Date(milestone.achieved_date), 'MMMM d, yyyy')}
                </p>
                {milestone.achieved_by_user && (
                  <p className="text-sm text-green-700">
                    by {milestone.achieved_by_user.first_name} {milestone.achieved_by_user.last_name}
                  </p>
                )}
                {milestone.verification_notes && (
                  <p className="text-sm text-green-700 mt-1">{milestone.verification_notes}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="assignments" className="space-y-4">
        <TabsList>
          <TabsTrigger value="assignments">
            <Users className="h-4 w-4 mr-2" />
            Assignments
          </TabsTrigger>
          <TabsTrigger value="progress">
            <TrendingUp className="h-4 w-4 mr-2" />
            Progress History
          </TabsTrigger>
          <TabsTrigger value="details">
            <FileText className="h-4 w-4 mr-2" />
            Details
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assignments">
          <MilestoneAssignments
            milestoneId={milestone.id}
            projectId={projectId}
            totalEquity={milestone.equity_amount || milestone.equity_percentage}
            isPercentageBased={!!milestone.equity_percentage}
          />
        </TabsContent>

        <TabsContent value="progress">
          <div className="space-y-4">
            {progressHistory && progressHistory.length > 0 ? (
              progressHistory.map((progress) => (
                <div key={progress.id} className="bg-white p-4 rounded-lg border">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">
                        {progress.progress_percentage}% Complete
                      </p>
                      <p className="text-sm text-gray-600">
                        {format(new Date(progress.progress_date), 'MMM d, yyyy')}
                      </p>
                      {progress.notes && (
                        <p className="text-sm text-gray-700 mt-2">{progress.notes}</p>
                      )}
                    </div>
                    {progress.progress_value && (
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Value</p>
                        <p className="font-medium">{formatValue(progress.progress_value)}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No progress updates yet</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="details">
          <div className="bg-white rounded-lg p-6 space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Type</p>
              <p className="mt-1">{milestone.milestone_type.replace('_', ' ')}</p>
            </div>
            {milestone.equity_class && (
              <div>
                <p className="text-sm font-medium text-gray-500">Equity Class</p>
                <p className="mt-1">
                  {milestone.equity_class.class_name}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-500">Options</p>
              <div className="mt-1 space-y-1">
                <Badge variant={milestone.is_retroactive ? 'default' : 'outline'}>
                  {milestone.is_retroactive ? 'Can be applied retroactively' : 'Not retroactive'}
                </Badge>
                <Badge variant={milestone.requires_board_approval ? 'default' : 'outline'}>
                  {milestone.requires_board_approval ? 'Requires board approval' : 'No board approval needed'}
                </Badge>
              </div>
            </div>
            {milestone.created_by_user && (
              <div>
                <p className="text-sm font-medium text-gray-500">Created by</p>
                <p className="mt-1">
                  {milestone.created_by_user.first_name} {milestone.created_by_user.last_name}
                </p>
                <p className="text-sm text-gray-600">
                  {format(new Date(milestone.created_at), 'MMM d, yyyy')}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Update Progress Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Progress</DialogTitle>
            <DialogDescription>
              Record the current progress towards this milestone
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="progress">Progress Percentage</Label>
              <Input
                id="progress"
                type="number"
                min="0"
                max="100"
                value={progressForm.progress_percentage || ''}
                onChange={(e) => setProgressForm({
                  ...progressForm,
                  progress_percentage: parseInt(e.target.value) || 0
                })}
                placeholder="0"
              />
              <p className="text-sm text-gray-600">
                Current: {currentProgress}% → New: {progressForm.progress_percentage || 0}%
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={progressForm.notes || ''}
                onChange={(e) => setProgressForm({ ...progressForm, notes: e.target.value })}
                placeholder="Describe the progress made..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgressDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => updateProgress(progressForm)}
              disabled={isUpdatingProgress || !progressForm.progress_percentage}
            >
              {isUpdatingProgress ? 'Updating...' : 'Update Progress'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Achieved Dialog */}
      <Dialog open={showAchieveDialog} onOpenChange={setShowAchieveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Milestone as Achieved</DialogTitle>
            <DialogDescription>
              This will trigger automatic equity grants based on the assignments
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Marking this milestone as achieved will:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Create equity offers for all assigned recipients</li>
                  <li>Record the achievement date as today</li>
                  <li>Notify relevant stakeholders</li>
                  {milestone.requires_board_approval && (
                    <li className="text-orange-600">Require board approval before grants are issued</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="achievement_notes">Achievement Notes</Label>
              <Textarea
                id="achievement_notes"
                value={achievementNotes}
                onChange={(e) => setAchievementNotes(e.target.value)}
                placeholder="Describe how the milestone was achieved..."
                rows={3}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAchieveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => markAsAchieved(achievementNotes)}
              disabled={isAchieving || !achievementNotes}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAchieving ? 'Processing...' : 'Confirm Achievement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}